import { Button, FilterBar, PageHeader, SelectField, StatusChip } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { OwnerDataTable as DataTable } from "../../components/owner-data-table.tsx";
import { useAnalyticsEvents } from "../../features/backoffice/queries.ts";

export const Route = createFileRoute("/_app/analytics")({ component: AnalyticsScreen });

// Locked catalog (docs/_spec-backbone.md §5). Filter-only list — the backend
// is the source of truth for validation; this just drives the dropdown.
const EVENT_NAMES = [
  "liff_opened",
  "venue_session_viewed",
  "age_gate_confirmed",
  "consent_accepted",
  "line_profile_prefilled",
  "table_joined",
  "privacy_mode_selected",
  "menu_viewed",
  "item_added_to_cart",
  "order_submitted",
  "order_accepted",
  "order_served",
  "around_viewed",
  "table_profile_viewed",
  "gift_started",
  "gift_sent",
  "gift_accepted",
  "gift_rejected",
  "gift_fulfilled",
  "contact_request_sent",
  "contact_request_accepted",
  "contact_request_rejected",
  "report_submitted",
  "block_user",
  "game_joined",
  "session_left",
  "screen_post_submitted",
  "screen_post_paid",
  "screen_post_approved",
  "screen_post_displayed",
  "screen_post_rejected",
] as const;

const PAGE_SIZE = 20;

function AnalyticsScreen() {
  const [eventName, setEventName] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAnalyticsEvents({
    eventName: eventName || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasNextPage = page * PAGE_SIZE < total;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Analytics events"
        subtitle="Real AEV-* event stream (PII-minimized). Filter by event name and page through history."
      />
      <FilterBar>
        <SelectField
          id="analytics-event-filter"
          label="Event name"
          value={eventName}
          onChange={(value) => {
            setEventName(value);
            setPage(1);
          }}
          options={[
            { value: "", label: "All events" },
            ...EVENT_NAMES.map((name) => ({ value: name, label: name })),
          ]}
        />
      </FilterBar>
      <DataTable
        rows={items}
        getRowKey={(row) => row.id}
        empty={
          <p className="text-muted-foreground text-sm">
            {isLoading ? "Loading events..." : "No analytics events yet for this filter."}
          </p>
        }
        columns={[
          {
            key: "event",
            header: "Event",
            cell: (row) => <StatusChip tone="info">{row.eventName}</StatusChip>,
          },
          { key: "actor", header: "Actor", cell: (row) => row.actorType },
          { key: "session", header: "Session", cell: (row) => row.venueSessionId ?? "—" },
          {
            key: "properties",
            header: "Properties",
            cell: (row) => JSON.stringify(row.properties),
          },
          { key: "time", header: "Received", cell: (row) => row.receivedAt },
        ]}
      />
      <div className="flex items-center justify-between text-muted-foreground text-sm">
        <span>{total} total events</span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNextPage}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
